import { useEffect, useRef, useState } from "react";
import { apiUrl } from "../lib/tenantOrigin";

type ResourceState<T> = { data: T | null; loading: boolean };

type ResourceEntry<T> = {
  url: string;
  state: ResourceState<T>;
  listeners: Set<() => void>;
  controller: AbortController | null;
  retryTimer: ReturnType<typeof setTimeout> | null;
  refreshTimer: ReturnType<typeof setTimeout> | null;
  inFlight: boolean;
  refreshQueued: boolean;
  lastRefreshAt: number;
  revision: unknown;
};

const RETRY_INTERVAL_MS = 1_000;
const MIN_REFRESH_INTERVAL_MS = 250;
const entries = new Map<string, ResourceEntry<unknown>>();

function publish(entry: ResourceEntry<unknown>) {
  for (const listener of entry.listeners) listener();
}

function requestRefresh(entry: ResourceEntry<unknown>) {
  if (entry.retryTimer) clearTimeout(entry.retryTimer);
  entry.retryTimer = null;
  if (entry.inFlight) {
    entry.refreshQueued = true;
    return;
  }
  const delay = MIN_REFRESH_INTERVAL_MS - (Date.now() - entry.lastRefreshAt);
  if (delay > 0) {
    if (entry.refreshTimer) return;
    entry.refreshTimer = setTimeout(() => {
      entry.refreshTimer = null;
      requestRefresh(entry);
    }, delay);
    return;
  }
  void refresh(entry);
}

function retry(entry: ResourceEntry<unknown>) {
  if (entry.listeners.size === 0 || entry.retryTimer) return;
  entry.retryTimer = setTimeout(() => {
    entry.retryTimer = null;
    requestRefresh(entry);
  }, RETRY_INTERVAL_MS);
}

async function refresh(entry: ResourceEntry<unknown>) {
  if (entry.listeners.size === 0) return;
  entry.inFlight = true;
  entry.lastRefreshAt = Date.now();
  const controller = new AbortController();
  entry.controller = controller;
  let failed = false;

  try {
    const res = await fetch(entry.url, {
      credentials: "include",
      signal: controller.signal,
    });
    if (res.ok) {
      entry.state = { data: await res.json(), loading: false };
    } else {
      failed = res.status >= 500;
      entry.state = { ...entry.state, loading: false };
    }
    publish(entry);
  } catch (err) {
    if (!(err instanceof Error && err.name === "AbortError")) {
      failed = true;
      entry.state = { ...entry.state, loading: false };
      publish(entry);
    }
  } finally {
    entry.inFlight = false;
    entry.controller = null;
    if (entry.listeners.size === 0) return;
    if (entry.refreshQueued) {
      entry.refreshQueued = false;
      requestRefresh(entry);
    } else if (failed) {
      retry(entry);
    }
  }
}

function subscribe<T>(
  key: string,
  revision: unknown,
  listener: () => void
): ResourceEntry<T> {
  let entry = entries.get(key) as ResourceEntry<T> | undefined;
  if (!entry) {
    entry = {
      url: key,
      state: { data: null, loading: true },
      listeners: new Set(),
      controller: null,
      retryTimer: null,
      refreshTimer: null,
      inFlight: false,
      refreshQueued: false,
      lastRefreshAt: 0,
      revision,
    };
    entries.set(key, entry as ResourceEntry<unknown>);
  }
  entry.listeners.add(listener);
  if (entry.listeners.size === 1)
    requestRefresh(entry as ResourceEntry<unknown>);
  if (!Object.is(entry.revision, revision)) {
    entry.revision = revision;
    requestRefresh(entry as ResourceEntry<unknown>);
  }
  return entry;
}

function unsubscribe(key: string, listener: () => void) {
  const entry = entries.get(key);
  if (!entry) return;
  entry.listeners.delete(listener);
  if (entry.listeners.size > 0) return;
  if (entry.retryTimer) clearTimeout(entry.retryTimer);
  if (entry.refreshTimer) clearTimeout(entry.refreshTimer);
  entry.controller?.abort();
  entries.delete(key);
}

export function useRevisionedJson<T>(
  path: string,
  search: string | null,
  revision?: unknown
): ResourceState<T> {
  const externalApiUrl = apiUrl();
  const key =
    search === null
      ? null
      : `${externalApiUrl}${path}${search ? `?${search}` : ""}`;
  const [state, setState] = useState<ResourceState<T>>({
    data: null,
    loading: key !== null,
  });
  const revisionAtSubscribe = useRef(revision);
  revisionAtSubscribe.current = revision;

  useEffect(() => {
    if (key === null) {
      setState({ data: null, loading: false });
      return;
    }
    const update = () => {
      const entry = entries.get(key) as ResourceEntry<T> | undefined;
      if (entry) setState(entry.state);
    };
    const entry = subscribe<T>(key, revisionAtSubscribe.current, update);
    setState(entry.state);
    return () => unsubscribe(key, update);
  }, [key]);

  useEffect(() => {
    if (key === null) return;
    const entry = entries.get(key);
    if (!entry || Object.is(entry.revision, revision)) return;
    entry.revision = revision;
    requestRefresh(entry);
  }, [key, revision]);

  return state;
}
